import { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useSearchParams, Link, useBlocker, useNavigate, useLocation } from 'react-router-dom';
import api from '../api/client';
import { fetchActiveLessonSession } from '../api/activeLesson';
import { useAuth } from '../context/AuthContext';
import UnifiedWorkspace from '../components/UnifiedWorkspace';
import {
  CollaborativeRunnerProvider,
  CollaborativeRunnerInputSection,
  CollaborativeRunnerOutputSection,
  OutputBlockRunInfo,
  useRunnerContext,
  EditorInputFilesBadge,
  RunnerTaskAttachmentsSync,
} from '../components/CollaborativeRunner';

/** Снимок runner для tests_result попытки (обновляется внутри CollaborativeRunnerProvider). */
function RunnerSnapshotFill({ payloadRef }) {
  const ctx = useRunnerContext();
  useEffect(() => {
    payloadRef.current = {
      runner: 'pyodide',
      lastRunStartedAt: ctx.lastRunStartedAt,
      lastRunFinishedAt: ctx.lastRunFinishedAt,
      lastRunDurationMs: ctx.lastRunDurationMs,
      stdout: ctx.output || '',
      stderr: ctx.error || '',
      stdin: ctx.stdin || '',
      files: ctx.files || {},
      codeAtLastRun: ctx.codeAtLastRun ?? null,
    };
  });
  return null;
}
import SolutionCodeBlock from '../components/SolutionCodeBlock';
import WorkPageBlocksLayout, {
  LAYOUT_PRESET_A,
  LAYOUT_PRESET_B,
  saveLayoutToStorage,
  loadLayoutFromStorage,
  layoutsEqual,
} from '../components/WorkPageBlocksLayout';
import WorkToolbar from '../components/WorkToolbar';
import Modal from '../components/Modal';
import StudentTaskReportModal from '../components/StudentTaskReportModal';
import MarkdownText from '../components/MarkdownText';
import CollaborativeTaskPanel, {
  CollaborativeTaskMarkBar,
  CollaborativeTaskProvider,
} from '../components/CollaborativeTaskPanel';
import TaskStatementMeta from '../components/TaskStatementMeta';
import TaskAnswerCheckPanel from '../components/TaskAnswerCheckPanel';
import ChoiceAnswerPanel from '../components/ChoiceAnswerPanel';
import AssembleAnswerPanel from '../components/AssembleAnswerPanel';
import StatementAttachmentLinks from '../components/StatementAttachmentLinks';
import { taskNavRef } from '../utils/taskNav';
import { loadGuestQuiz } from '../utils/guestPractice';
import {
  buildAttemptSnapshot,
  fetchLatestAttempt,
  isChoiceAnswerType,
  isOrderAnswerType,
  resolveWorkspace,
  recordTaskAttempt,
} from '../utils/workAttemptHelpers';
import { useTabIdentity } from '../hooks/useTabIdentity';
import { badgeFromLabel } from '../utils/tabIdentity';

/** Интервал поллинга позиции ученика (REST work-presence); без WebSocket — проще и согласовано с POST /tasks/presence/. */
const MENTOR_FOLLOW_POLL_MS = 4000;

function normalizeWorkPathForFollow(relativePathAndQuery) {
  const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  const u = new URL(relativePathAndQuery, base);
  u.searchParams.delete('mentor');
  u.searchParams.delete('student');
  u.searchParams.delete('follow');
  const q = u.searchParams.toString();
  return `${u.pathname}${q ? `?${q}` : ''}`;
}

function withMentorFollowQuery(targetRelative, studentId, follow) {
  const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  const u = new URL(targetRelative, base);
  u.searchParams.set('mentor', '1');
  u.searchParams.set('student', String(studentId));
  if (follow) u.searchParams.set('follow', '1');
  else u.searchParams.delete('follow');
  return `${u.pathname}${u.search}`;
}

const TEACHER_VERDICT_LABELS = {
  ACCEPTED: 'Принять',
  REVIEW_IN_LESSON: 'На занятии',
  REDO: 'Переделать',
};

/** Временно скрыто: кнопки «предыдущая / следующая задача» в боковом списке. */
const SHOW_TASK_LIST_PREV_NEXT = false;

/**
 * Сохранение попытки в шапке редактора (внутри CollaborativeRunnerProvider).
 */
function AttemptSaveHeaderButton({
  user,
  task,
  taskSlug,
  listItemId,
  lessonSessionId,
  currentCode,
  workspaceState = {},
  studentTextAnswer = '',
  runnerPayloadRef,
  onBaselineSynced,
  onAfterSave,
  attemptBaseline,
  attemptBaselineReady,
  gateDirtyByBaseline,
}) {
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [savedOk, setSavedOk] = useState(false);
  const {
    output,
    error,
    lastRunStartedAt,
    lastRunFinishedAt,
    lastRunDurationMs,
    stdin,
    files,
    codeAtLastRun,
  } = useRunnerContext();

  const snapshot = buildAttemptSnapshot(currentCode, studentTextAnswer, workspaceState);
  const hasDirtyChanges = attemptBaselineReady && snapshot !== attemptBaseline;
  const canClick =
    Boolean(user && task?.id) &&
    !saving &&
    (!gateDirtyByBaseline || hasDirtyChanges);

  async function saveAttempt() {
    if (!user || !task?.id || !canClick) return;
    setSaving(true);
    setSaveError('');
    setSavedOk(false);
    try {
      const runnerBase =
        runnerPayloadRef?.current && typeof runnerPayloadRef.current === 'object'
          ? { ...runnerPayloadRef.current }
          : {
              runner: 'pyodide',
              lastRunStartedAt,
              lastRunFinishedAt,
              lastRunDurationMs,
              stdout: output || '',
              stderr: error || '',
              stdin: stdin || '',
              files: files || {},
              codeAtLastRun: codeAtLastRun || null,
            };
      if (listItemId) {
        await recordTaskAttempt(api, {
          taskSlug,
          taskId: task.id,
          listItemId,
          lessonSessionId,
          codeSnapshot: currentCode,
          workspaceState,
          studentTextAnswer,
          testsResult: runnerBase,
        });
      } else {
        await api.post('/tasks/attempts/', {
          task: task.id,
          lesson_session: lessonSessionId ? Number(lessonSessionId) : null,
          task_list_item: null,
          code_snapshot: String(currentCode ?? ''),
          workspace_state: workspaceState && typeof workspaceState === 'object' ? workspaceState : {},
          tests_result: runnerBase,
          self_report_status: 'NOT_TRIED',
          self_comment: '',
        });
      }
      setSavedOk(true);
      if (typeof onBaselineSynced === 'function') onBaselineSynced();
      if (typeof onAfterSave === 'function') onAfterSave();
      setTimeout(() => setSavedOk(false), 2000);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Не удалось сохранить попытку.';
      setSaveError(typeof msg === 'string' ? msg : 'Не удалось сохранить попытку.');
    } finally {
      setSaving(false);
    }
  }

  let title = 'Сохранить попытку (код, запуск и вывод)';
  if (saving) title = 'Сохранение…';
  else if (gateDirtyByBaseline && !attemptBaselineReady) title = 'Подождите, загружается состояние…';
  else if (gateDirtyByBaseline && !hasDirtyChanges) title = 'Нет изменений для сохранения';
  else if (!user || !task?.id) title = 'Войдите в аккаунт, чтобы сохранять попытки';

  return (
    <span className="work-attempt-save-header" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap' }}>
      <button
        type="button"
        className="btn btn-outline btn-xs btn-icon-only work-block-title-action"
        onClick={() => void saveAttempt()}
        disabled={!canClick}
        title={title}
        aria-label={title}
      >
        <i className="fa-solid fa-floppy-disk ui-icon" aria-hidden />
      </button>
      {savedOk ? (
        <span className="text-success" style={{ fontSize: '0.75rem' }} aria-live="polite">
          Сохранено
        </span>
      ) : null}
      {saveError ? (
        <span className="text-danger" style={{ fontSize: '0.75rem', maxWidth: '12rem' }} aria-live="polite">
          {saveError}
        </span>
      ) : null}
    </span>
  );
}

function normalizeEditorSettings(settings) {
  const source = settings && typeof settings === 'object' ? settings : {};
  return {
    ...source,
    lineWrapping: source.lineWrapping === true,
    taskTextWrapping: source.taskTextWrapping !== false,
    workStatementFontScale: typeof source.workStatementFontScale === 'number' ? source.workStatementFontScale : 1,
    workSidebarFontScale: typeof source.workSidebarFontScale === 'number' ? source.workSidebarFontScale : 1,
  };
}

/**
 * Единая страница работы с задачей. Редактор всегда совместный (одна сессия на задачу/приглашение).
 * /work/:taskSlug, /sessions/:lessonSessionId/tasks/:taskSlug.
 */
export default function WorkPage() {
  const { taskSlug, lessonSessionId } = useParams();
  const [searchParams] = useSearchParams();
  const listId = searchParams.get('list');
  const isGuestQuiz = searchParams.get('guest') === '1';
  const collabFromUrl = searchParams.get('collab');
  const mentorFollow = searchParams.get('mentor') === '1';
  const followStudentParam = searchParams.get('student');
  const followEnabled = searchParams.get('follow') === '1';
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isMentorFollowMode = Boolean(user?.is_teacher && mentorFollow);
  const [followJumpNotice, setFollowJumpNotice] = useState('');
  const [task, setTask] = useState(null);
  const [code, setCode] = useState('');
  const [workspaceState, setWorkspaceState] = useState({});
  const [seedWorkspaceState, setSeedWorkspaceState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showHints, setShowHints] = useState(false);
  const [collabSessionId, setCollabSessionId] = useState(null);
  const [ydoc, setYdoc] = useState(null);
  const [taskList, setTaskList] = useState(null);
  /** Список по ?list= уже пришёл (или его нет) — не создавать collab-сессию «без пункта», которую потом сменим. */
  const [taskListReady, setTaskListReady] = useState(() => !listId && !isGuestQuiz);
  const [similarTasks, setSimilarTasks] = useState([]);
  const [showSolution, setShowSolution] = useState(false);
  const [teacherWatching, setTeacherWatching] = useState(false);
  const runRequestRef = useRef(null);
  const editorRef = useRef(null);
  const [collabConnected, setCollabConnected] = useState(false);
  const [collabUserCount, setCollabUserCount] = useState(0);
  const [styleLintEnabled, setStyleLintEnabled] = useState(true);
  const [formatting, setFormatting] = useState(false);
  const [layoutEditMode, setLayoutEditMode] = useState(false);
  const layoutStorageKey = user ? `workLayout:${user.username}` : 'workLayout:guest';
  const taskStatementStateKey = user ? `workTaskStatementState:${user.username}` : 'workTaskStatementState:guest';
  const taskListStateKey = user ? `workTaskListState:${user.username}` : 'workTaskListState:guest';
  const [layout, setLayout] = useState(LAYOUT_PRESET_A);
  const [taskStatementCollapsed, setTaskStatementCollapsed] = useState(false);
  const [taskStatementCollapsedHeight, setTaskStatementCollapsedHeight] = useState(6);
  const [taskListCollapsed, setTaskListCollapsed] = useState(false);
  const [taskListCollapsedHeight, setTaskListCollapsedHeight] = useState(9);
  const [inputModalOpen, setInputModalOpen] = useState(false);
  const [wrapSaving, setWrapSaving] = useState(false);
  const [editorThemeSaving, setEditorThemeSaving] = useState(false);
  const [wrapSaveError, setWrapSaveError] = useState('');
  const [listFinishSaving, setListFinishSaving] = useState(false);
  const [listFinishError, setListFinishError] = useState('');
  const [reportModalItem, setReportModalItem] = useState(null);
  const [activeLessonFromApi, setActiveLessonFromApi] = useState(null);
  const lastAttemptContextRef = useRef('');
  const runnerPayloadRef = useRef({});
  const taskListScrollRef = useRef(null);
  /** Актуальный код редактора для baseline навигации (после await fetch попытки state code может отставать от Yjs). */
  const codeForAttemptBaselineRef = useRef('');
  /** Одноразовая подстановка кода из последней попытки в пустой Yjs-документ. */
  const attemptSeedAppliedRef = useRef('');
  /** После сида один раз выровнять чекпоинт навигации по актуальному `code` из редактора. */
  const pendingNavBaselineResyncKeyRef = useRef('');
  const [studentAnswer, setStudentAnswer] = useState('');
  const [checkRevealSaving, setCheckRevealSaving] = useState(false);
  const [attemptBaseline, setAttemptBaseline] = useState('');
  /** Не сравнивать снимок попытки с baseline до загрузки baseline для текущей задачи (иначе ложное «грязное» состояние). */
  const [attemptBaselineReady, setAttemptBaselineReady] = useState(false);
  const [listManageSaving, setListManageSaving] = useState(false);
  const [listManageError, setListManageError] = useState('');
  const [addTaskSearch, setAddTaskSearch] = useState('');
  const [addTaskResults, setAddTaskResults] = useState([]);
  const [addTaskSearching, setAddTaskSearching] = useState(false);
  const [rowTeacherDraft, setRowTeacherDraft] = useState({});

  const [editorSettings, setEditorSettings] = useState(() => normalizeEditorSettings(user?.editor_settings));
  useEffect(() => {
    setEditorSettings(normalizeEditorSettings(user?.editor_settings));
  }, [user?.editor_settings]);
  const editorTheme = editorSettings?.themeId ?? 'dark';
  const taskTextWrapping = editorSettings.taskTextWrapping !== false;
  const hintsEnabled = editorSettings.showHints === true;
  const workStatementFontScale = editorSettings?.workStatementFontScale ?? 1;
  const workSidebarFontScale = editorSettings?.workSidebarFontScale ?? 1;

  const studentIdForFollow = useMemo(() => {
    const fromQ =
      followStudentParam != null && followStudentParam !== ''
        ? Number(followStudentParam)
        : NaN;
    if (Number.isFinite(fromQ) && fromQ > 0) return fromQ;
    const sid = taskList?.student_id;
    const n = sid != null ? Number(sid) : NaN;
    if (Number.isFinite(n) && n > 0) return n;
    return null;
  }, [followStudentParam, taskList?.student_id]);

  codeForAttemptBaselineRef.current = code ?? '';
  const workspaceStateRef = useRef(workspaceState);
  workspaceStateRef.current = workspaceState;

  useEffect(() => {
    if (layoutStorageKey) setLayout(loadLayoutFromStorage(layoutStorageKey));
  }, [layoutStorageKey]);

  useEffect(() => {
    if (!taskStatementStateKey) return;
    try {
      const s = localStorage.getItem(taskStatementStateKey);
      if (s) {
        const parsed = JSON.parse(s);
        if (typeof parsed?.collapsed === 'boolean') setTaskStatementCollapsed(parsed.collapsed);
        if (typeof parsed?.lastH === 'number' && parsed.lastH >= 1) setTaskStatementCollapsedHeight(parsed.lastH);
      }
    } catch {
      // ignore
    }
  }, [taskStatementStateKey]);

  useEffect(() => {
    if (!taskListStateKey) return;
    try {
      const s = localStorage.getItem(taskListStateKey);
      if (s) {
        const parsed = JSON.parse(s);
        if (typeof parsed?.collapsed === 'boolean') setTaskListCollapsed(parsed.collapsed);
        if (typeof parsed?.lastH === 'number' && parsed.lastH >= 1) setTaskListCollapsedHeight(parsed.lastH);
      }
    } catch {
      // ignore
    }
  }, [taskListStateKey]);

  function saveTaskStatementState(collapsed, lastH) {
    try {
      localStorage.setItem(taskStatementStateKey, JSON.stringify({ collapsed, lastH }));
    } catch {
      // ignore
    }
  }

  function saveTaskListState(collapsed, lastH) {
    try {
      localStorage.setItem(taskListStateKey, JSON.stringify({ collapsed, lastH }));
    } catch {
      // ignore
    }
  }

  const isCollaborative = Boolean(lessonSessionId) || Boolean(activeLessonFromApi?.id);

  useEffect(() => {
    if (!user || isMentorFollowMode) {
      setActiveLessonFromApi(null);
      return;
    }
    let cancelled = false;
    fetchActiveLessonSession()
      .then((active) => {
        if (!cancelled) setActiveLessonFromApi(active);
      })
      .catch(() => {
        if (!cancelled) setActiveLessonFromApi(null);
      });
    return () => { cancelled = true; };
  }, [user, isMentorFollowMode]);

  const hasLoadedTaskRef = useRef(false);
  const fetchTask = useCallback(async () => {
    if (!taskSlug) return;
    const firstLoad = !hasLoadedTaskRef.current;
    if (firstLoad) setLoading(true);
    setError('');
    try {
      const res = await api.get(`/tasks/${taskSlug}/`);
      setTask(res.data);
      if (firstLoad) {
        setCode(res.data.initial_code || res.data.template_code || '# Введите ваше решение здесь\n');
      }
      setShowSolution(false);
      hasLoadedTaskRef.current = true;
    } catch {
      setError('Задача не найдена');
    } finally {
      if (firstLoad) setLoading(false);
    }
  }, [taskSlug]);

  useEffect(() => {
    if (taskSlug) void fetchTask();
  }, [taskSlug, fetchTask]);

  useEffect(() => {
    if (isGuestQuiz) {
      setTaskList(loadGuestQuiz());
      setTaskListReady(true);
      return undefined;
    }
    if (!listId) {
      setTaskList(null);
      setTaskListReady(true);
      return undefined;
    }
    let cancelled = false;
    setTaskListReady(false);
    api.get(`/tasks/lists/${listId}/`).then((res) => {
      if (!cancelled) setTaskList(res.data);
    }).catch(() => {
      if (!cancelled) setTaskList(null);
    }).finally(() => {
      if (!cancelled) setTaskListReady(true);
    });
    return () => { cancelled = true; };
  }, [listId, isGuestQuiz]);

  // Периодическое обновление списка, чтобы ученик видел задачи, добавленные преподавателем на лету
  useEffect(() => {
    if (!listId || isGuestQuiz) return;
    const id = setInterval(() => {
      api.get(`/tasks/lists/${listId}/`).then((res) => setTaskList(res.data)).catch(() => {});
    }, 30000);
    return () => clearInterval(id);
  }, [listId, isGuestQuiz]);

  useEffect(() => {
    if (taskSlug) {
      api
        .get(`/tasks/${taskSlug}/similar/`)
        .then((res) => setSimilarTasks(Array.isArray(res.data) ? res.data : res.data?.results ?? []))
        .catch(() => setSimilarTasks([]));
    } else {
      setSimilarTasks([]);
    }
  }, [taskSlug]);

  const currentListItemId = useMemo(() => {
    if (!taskList?.items?.length || !taskSlug) return null;
    const found = taskList.items.find((i) => {
      const ref = i.task ? taskNavRef(i.task) : String(i.task_id ?? '');
      return ref === taskSlug || String(i.task_id) === taskSlug;
    });
    return found?.id ?? null;
  }, [taskList, taskSlug]);

  /** Свой список (в т.ч. пробный тест): не только роль «ученик», но и преподаватель, решивший «Решать». */
  const isOwnListWorker = Boolean(
    listId &&
      user?.id != null &&
      taskList?.student_id != null &&
      Number(taskList.student_id) === Number(user.id) &&
      !isMentorFollowMode,
  );

  /** Сохранять попытки/черновики: свой список или ученик вне списка. */
  const canTrackOwnWork = Boolean(
    !isMentorFollowMode && (isOwnListWorker || (user?.is_student && !listId)),
  );

  const taskListScrollStorageKey = listId ? `workTaskListScroll:${listId}` : null;

  useLayoutEffect(() => {
    const el = taskListScrollRef.current;
    if (!el || !taskListScrollStorageKey) return;
    const raw = sessionStorage.getItem(taskListScrollStorageKey);
    if (raw == null || raw === '') return;
    const top = Number(raw);
    if (!Number.isFinite(top)) return;
    el.scrollTop = top;
  }, [taskSlug, listId, taskListScrollStorageKey, taskList?.items?.length]);

  function saveTaskListScroll() {
    const el = taskListScrollRef.current;
    if (!el || !taskListScrollStorageKey) return;
    sessionStorage.setItem(taskListScrollStorageKey, String(el.scrollTop));
  }

  function getCurrentTaskListItemId() {
    return currentListItemId;
  }

  function getWorkDraftStorageKey() {
    if (!user || !taskSlug) return '';
    const uid = user.id ?? user.username ?? 'guest';
    return `draft:${uid}:work:${taskSlug}`;
  }

  async function clearWorkDraftsOnAbandon() {
    const lsKey = getWorkDraftStorageKey();
    if (lsKey) {
      try {
        localStorage.removeItem(lsKey);
      } catch {
        /* ignore */
      }
    }
    if (!user || !taskSlug) return;
    try {
      await api.put('/tasks/drafts/', {
        task: taskSlug,
        task_list_item: getCurrentTaskListItemId(),
        code: '',
      });
    } catch {
      /* ignore */
    }
  }

  useEffect(() => {
    attemptSeedAppliedRef.current = '';
    pendingNavBaselineResyncKeyRef.current = '';
  }, [taskSlug, currentListItemId]);

  useEffect(() => {
    const key = `${taskSlug || ''}|${currentListItemId ?? 'none'}`;
    if (pendingNavBaselineResyncKeyRef.current !== key) return;
    pendingNavBaselineResyncKeyRef.current = '';
    setAttemptBaseline(
      buildAttemptSnapshot(codeForAttemptBaselineRef.current, studentAnswer, workspaceStateRef.current),
    );
  }, [code, studentAnswer, taskSlug, currentListItemId]);

  useEffect(() => {
    // При смене задачи (или пункта ДЗ) сбрасываем поля отчёта/комментария,
    // чтобы они не переносились между задачами.
    const itemId = currentListItemId;
    const key = `${taskSlug || ''}|${listId || ''}|${itemId || ''}`;
    if (key === lastAttemptContextRef.current) return;
    lastAttemptContextRef.current = key;
  }, [taskSlug, listId, currentListItemId]);

  /** Чекпоинт навигации: свой список или ученик вне списка. */
  const navCheckpointContext = Boolean(
    canTrackOwnWork &&
      taskSlug &&
      (!listId || currentListItemId),
  );
  const hasUnsavedWorkForNavigation =
    navCheckpointContext &&
    attemptBaselineReady &&
    buildAttemptSnapshot(code, studentAnswer, workspaceState) !== attemptBaseline;
  const hasNavigationBlock = Boolean(hasUnsavedWorkForNavigation);

  useEffect(() => {
    let cancelled = false;
    if (!taskSlug || !canTrackOwnWork) {
      setAttemptBaseline('');
      setStudentAnswer('');
      setAttemptBaselineReady(true);
      return () => {
        cancelled = true;
      };
    }
    if (listId && !currentListItemId) {
      setAttemptBaselineReady(false);
      return () => {
        cancelled = true;
      };
    }
    if (loading || !task || task.slug !== taskSlug) {
      setAttemptBaselineReady(false);
      return () => {
        cancelled = true;
      };
    }
    setAttemptBaselineReady(false);
    const listItemId = currentListItemId;
    (async () => {
      const applyBaselineFromEditor = (ans, wsState) => {
        queueMicrotask(() => {
          if (cancelled) return;
          setAttemptBaseline(
            buildAttemptSnapshot(codeForAttemptBaselineRef.current, ans, wsState),
          );
          setAttemptBaselineReady(true);
        });
      };
      try {
        const la = await fetchLatestAttempt(api, taskSlug, listItemId);
        if (cancelled) return;
        const ans = la ? String(la.tests_result?.student_text_answer ?? '') : '';
        const ws =
          la?.workspace_state && typeof la.workspace_state === 'object'
            ? la.workspace_state
            : {};
        setStudentAnswer(ans);
        setWorkspaceState(ws);
        setSeedWorkspaceState(Object.keys(ws).length ? ws : null);
        applyBaselineFromEditor(ans, ws);
      } catch {
        if (!cancelled) {
          setStudentAnswer('');
          setWorkspaceState({});
          setSeedWorkspaceState(null);
          applyBaselineFromEditor('', {});
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [taskSlug, currentListItemId, listId, canTrackOwnWork, loading, task?.slug, task]);

  useEffect(() => {
    if (!canTrackOwnWork) return;
    if (!taskSlug || !task || task.slug !== taskSlug || loading) return;
    if (listId && !currentListItemId) return;
    if (!ydoc) return;

    const key = `${taskSlug}|${currentListItemId ?? 'none'}`;
    if (attemptSeedAppliedRef.current === key) return;

    let cancelled = false;
    (async () => {
      try {
        const la = await fetchLatestAttempt(api, taskSlug, currentListItemId);
        if (cancelled || !la) return;
        const snap = String(la.code_snapshot ?? '');
        if (!snap) return;
        const ytext = ydoc.getText('codemirror');
        if (ytext.length > 0) {
          attemptSeedAppliedRef.current = key;
          return;
        }
        ydoc.transact(() => {
          ytext.insert(0, snap);
        });
        attemptSeedAppliedRef.current = key;
        pendingNavBaselineResyncKeyRef.current = key;
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    canTrackOwnWork,
    taskSlug,
    task,
    loading,
    listId,
    currentListItemId,
    ydoc,
  ]);

  useEffect(() => {
    if (!hasNavigationBlock) return;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [hasNavigationBlock]);

  const navBlocker = useBlocker(hasNavigationBlock);
  useEffect(() => {
    if (navBlocker.state !== 'blocked') return;
    const leave = window.confirm(
      'Есть несохранённые изменения (код или ответ в условии). Уйти без сохранения?',
    );
    if (leave) {
      void clearWorkDraftsOnAbandon();
      navBlocker.proceed();
    } else navBlocker.reset();
  }, [navBlocker.state, navBlocker]);

  async function handleToggleWrapping() {
    if (!user || wrapSaving) return;
    const previousSettings = editorSettings;
    const nextWrap = !taskTextWrapping;
    const nextSettings = {
      ...previousSettings,
      lineWrapping: nextWrap,
      taskTextWrapping: nextWrap,
    };
    setWrapSaveError('');
    setEditorSettings(nextSettings);
    setWrapSaving(true);
    try {
      await api.patch('/auth/profile/', { editor_settings: nextSettings });
    } catch {
      setEditorSettings(previousSettings);
      setWrapSaveError('Не удалось сохранить настройку переноса.');
    } finally {
      setWrapSaving(false);
    }
  }

  // Сохранено для будущей смены темы из тулбара; пока тема берётся из профиля.
  // eslint-disable-next-line no-unused-vars
  async function handleSetEditorTheme(themeId) {
    if (!user || editorThemeSaving || editorTheme === themeId) return;
    const previousSettings = editorSettings;
    const nextSettings = { ...previousSettings, themeId };
    setWrapSaveError('');
    setEditorSettings(nextSettings);
    setEditorThemeSaving(true);
    try {
      await api.patch('/auth/profile/', { editor_settings: nextSettings });
    } catch {
      setEditorSettings(previousSettings);
      setWrapSaveError('Не удалось сохранить тему редактора.');
    } finally {
      setEditorThemeSaving(false);
    }
  }

  // Совместный workspace (код или инструмент) — не для квизов/тестов.
  useEffect(() => {
    if (!task?.id) return;
    const ws = resolveWorkspace(task);
    if (!ws.showCollab) {
      setCollabSessionId(null);
      setYdoc(null);
      setCollabConnected(false);
      setCollabUserCount(0);
      return;
    }
    if (listId && !taskListReady) return;
    if (collabFromUrl) {
      setCollabSessionId(collabFromUrl);
    } else {
      getOrCreateCollabSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.id, task?.answer_type, task?.workspace_kind, collabFromUrl, currentListItemId, listId, taskListReady]);

  useEffect(() => {
    if (!taskSlug || !user) return;
    const tid = listId ? Number(listId) : null;
    api
      .post('/tasks/presence/', {
        task_slug: taskSlug,
        lesson_session_id: lessonSessionId ? Number(lessonSessionId) : null,
        task_list_id: Number.isFinite(tid) && tid > 0 ? tid : null,
        collaborative_session_id: collabSessionId || null,
      })
      .catch(() => {});
  }, [taskSlug, lessonSessionId, user, listId, collabSessionId]);

  useEffect(() => {
    if (!user) return;
    const tick = () => {
      api.get('/tasks/teacher-watching/').then((res) => {
        setTeacherWatching(Boolean(res.data?.teacher_watching));
      }).catch(() => {});
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [user]);

  useEffect(() => {
    if (!followJumpNotice) return;
    const t = setTimeout(() => setFollowJumpNotice(''), 6000);
    return () => clearTimeout(t);
  }, [followJumpNotice]);

  /**
   * Следование за учеником: поллинг GET work-presence (не меняет семантику teacher-watching для ученика).
   * При смене задачи — replace navigate; CollaborativeEditor переподключается по новому sessionId.
   */
  useEffect(() => {
    if (!isMentorFollowMode || !studentIdForFollow || !followEnabled) return undefined;

    const tick = async () => {
      try {
        const res = await api.get(`/teacher/students/${studentIdForFollow}/work-presence/`);
        const target = res.data?.target_url;
        if (!target || typeof target !== 'string') return;
        const normalizedRemote = normalizeWorkPathForFollow(target);
        const normalizedLocal = normalizeWorkPathForFollow(
          `${location.pathname}${location.search}`,
        );
        if (normalizedRemote === normalizedLocal) return;
        setFollowJumpNotice('Ученик перешёл к другой задаче. Редактор переключён на её коллаб-сессию.');
        navigate(withMentorFollowQuery(target, studentIdForFollow, true), { replace: true });
      } catch {
        /* ignore */
      }
    };

    const id = setInterval(tick, MENTOR_FOLLOW_POLL_MS);
    void tick();
    return () => clearInterval(id);
  }, [
    isMentorFollowMode,
    studentIdForFollow,
    followEnabled,
    navigate,
    location.pathname,
    location.search,
  ]);

  async function getOrCreateCollabSession() {
    if (!task?.id) return;
    try {
      const body = { task_id: task.id };
      if (currentListItemId) body.task_list_item_id = currentListItemId;
      const res = await api.post('/collaborative/sessions/', body);
      const sid = res.data?.session_id ?? res.data?.id;
      if (sid) setCollabSessionId(String(sid));
    } catch (e) {
      console.error('Collaborative session create failed', e);
    }
  }

  useEffect(() => {
    const handler = (e) => {
      if ((e.key === 'Enter' || e.code === 'Enter') && (e.ctrlKey || e.metaKey)) {
        const active = document.activeElement;
        if (active?.closest?.('.task-work-editor')) {
          e.preventDefault();
          runRequestRef.current?.run?.();
        }
      }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, []);

  const startMentorFollow = useCallback(() => {
    if (!studentIdForFollow) return;
    navigate(
      withMentorFollowQuery(`${location.pathname}${location.search}`, studentIdForFollow, true),
      { replace: true },
    );
  }, [studentIdForFollow, navigate, location.pathname, location.search]);

  const stopMentorFollow = useCallback(() => {
    if (!studentIdForFollow) return;
    navigate(
      withMentorFollowQuery(`${location.pathname}${location.search}`, studentIdForFollow, false),
      { replace: true },
    );
  }, [studentIdForFollow, navigate, location.pathname, location.search]);

  // тема редактора теперь берётся из user.editor_settings (страница /settings/editor)

  const workTabIdentity = useMemo(() => {
    const title = (task?.title || taskSlug || '').trim();
    if (!title) return null;
    const short = title.length > 48 ? `${title.slice(0, 45)}…` : title;
    const studentKey = followStudentParam || taskList?.student_id;
    return {
      title: short,
      badge: badgeFromLabel(title, 'З'),
      colorKey: studentKey ? `student:${studentKey}` : 'work',
    };
  }, [task?.title, taskSlug, followStudentParam, taskList?.student_id]);

  useTabIdentity(workTabIdentity);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Загрузка задачи...</p>
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="error-page">
        <h2>Ошибка</h2>
        <p>{error || 'Задача не найдена'}</p>
      </div>
    );
  }

  const taskStatementTitle = task.title;

  const canUseListAttempts = Boolean(isOwnListWorker && currentListItemId);

  async function persistAttemptWithTests(testsExtra, attemptExtra = {}) {
    if (!canUseListAttempts || !task?.id) return;
    setCheckRevealSaving(true);
    try {
      const base =
        runnerPayloadRef.current && typeof runnerPayloadRef.current === 'object'
          ? { ...runnerPayloadRef.current }
          : {};
      await recordTaskAttempt(api, {
        taskSlug,
        taskId: task.id,
        listItemId: currentListItemId,
        lessonSessionId,
        codeSnapshot: code,
        studentTextAnswer: studentAnswer,
        selfReportStatus: attemptExtra.selfReportStatus,
        testsResult: { ...base, ...testsExtra },
      });
      setAttemptBaseline(buildAttemptSnapshot(code, studentAnswer, workspaceState));
      if (listId) {
        api.get(`/tasks/lists/${listId}/`).then((res) => setTaskList(res.data)).catch(() => {});
      }
    } catch (e) {
      console.error(e);
    } finally {
      setCheckRevealSaving(false);
    }
  }

  async function handleCheckAnswer({ ok }) {
    if (!task.expected_answer) return;
    if (!canUseListAttempts) return;
    await persistAttemptWithTests(
      {
        answer_check_ok: ok,
        answer_checked_at: new Date().toISOString(),
        answer_revealed: false,
      },
      ok ? { selfReportStatus: 'OK_NO_PEEK' } : {},
    );
  }

  async function handleRevealAnswer() {
    if (!canUseListAttempts) return;
    await persistAttemptWithTests({
      answer_revealed: true,
      answer_revealed_at: new Date().toISOString(),
    });
  }

  const workUrl = (slug) => {
    const params = new URLSearchParams();
    if (listId) params.set('list', listId);
    if (isGuestQuiz) params.set('guest', '1');
    if (collabSessionId && slug === taskSlug) params.set('collab', collabSessionId);
    const qs = params.toString();
    if (lessonSessionId) {
      return `/sessions/${lessonSessionId}/tasks/${slug}${qs ? `?${qs}` : ''}`;
    }
    return `/work/${slug}${qs ? `?${qs}` : ''}`;
  };

  const statement = (
    <div className="task-statement">
      {!isMentorFollowMode && similarTasks.length > 0 ? (
        <div className="work-statement-similar" role="note">
          <span className="work-statement-similar-label">Похожие задачи:</span>
          <span className="work-statement-similar-links">
            {similarTasks.map((st) => {
              const stRef = taskNavRef(st);
              const isCurrent = stRef === taskSlug || String(st.id) === taskSlug;
              return (
                <span key={st.id} className="work-statement-similar-item">
                  <Link
                    to={workUrl(stRef)}
                    className={isCurrent ? 'work-statement-similar-link is-current' : 'work-statement-similar-link'}
                  >
                    {st.title || stRef}
                  </Link>
                </span>
              );
            })}
          </span>
        </div>
      ) : null}
      <div key={taskSlug} className="work-task-statement-switch-anim">
        <CollaborativeTaskPanel embedded />
      </div>
      {(task.solution_explanation || task.solution_code) && (
        <div className="task-answer-solution">
          <div className="solution-block">
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={() => setShowSolution((prev) => !prev)}
            >
              {showSolution ? 'Скрыть разбор' : 'Показать решение / разбор'}
            </button>
            {showSolution && (
              <div className="solution-text">
                {task.solution_explanation && (
                  <MarkdownText className="statement-text" text={task.solution_explanation} />
                )}
                {task.solution_code && (
                  <SolutionCodeBlock code={task.solution_code} theme={editorTheme} editorSettings={editorSettings} />
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const hintsSection = hintsEnabled && task.hints?.length > 0 ? (
    <div className="hints-section">
      <button
        className="btn btn-outline btn-sm"
        onClick={() => setShowHints(!showHints)}
      >
        {showHints ? '🙈 Скрыть подсказки' : '💡 Показать подсказки'}
      </button>
      {showHints && (
        <div className="hints-list">
          {task.hints.map((hint, i) => (
            <div key={i} className="hint-item">
              <strong>Подсказка {i + 1}:</strong> {typeof hint === 'string' ? hint : hint.text}
            </div>
          ))}
        </div>
      )}
    </div>
  ) : null;

  const canTeacherManageList = Boolean(
    isMentorFollowMode && taskList?.teacher_id && user?.id === taskList.teacher_id,
  );

  async function searchListTasks() {
    if (!addTaskSearch.trim()) return;
    setAddTaskSearching(true);
    try {
      const res = await api.get('/tasks/editor/', { params: { search: addTaskSearch.trim() } });
      const data = Array.isArray(res.data) ? res.data : res.data?.results ?? [];
      setAddTaskResults(data.slice(0, 20));
    } catch {
      setAddTaskResults([]);
    } finally {
      setAddTaskSearching(false);
    }
  }

  async function appendTaskToListById(taskPk) {
    if (!canTeacherManageList || !taskList?.id) return;
    setListManageSaving(true);
    setListManageError('');
    try {
      const items = taskList.items.map((it, idx) => ({
        task_id: it.task?.id ?? it.task_id,
        position: idx,
        teacher_note: it.teacher_note || '',
      }));
      items.push({ task_id: taskPk, position: items.length, teacher_note: '' });
      await api.patch(`/tasks/lists/${taskList.id}/`, { items });
      const res = await api.get(`/tasks/lists/${taskList.id}/`);
      setTaskList(res.data);
      setAddTaskResults([]);
      setAddTaskSearch('');
    } catch {
      setListManageError('Не удалось добавить задачу.');
    } finally {
      setListManageSaving(false);
    }
  }

  async function removeListItemById(itemId) {
    if (!canTeacherManageList || !taskList?.id) return;
    setListManageSaving(true);
    setListManageError('');
    try {
      const items = taskList.items
        .filter((i) => i.id !== itemId)
        .map((it, idx) => ({
          task_id: it.task?.id ?? it.task_id,
          position: idx,
          teacher_note: it.teacher_note || '',
        }));
      await api.patch(`/tasks/lists/${taskList.id}/`, { items });
      const res = await api.get(`/tasks/lists/${taskList.id}/`);
      setTaskList(res.data);
    } catch {
      setListManageError('Не удалось удалить задачу.');
    } finally {
      setListManageSaving(false);
    }
  }

  async function patchTeacherRowAttempt(attemptId, body) {
    const sid = taskList?.student_id;
    if (!sid || !attemptId) return;
    try {
      await api.patch(`/teacher/students/${sid}/attempts/${attemptId}/`, body);
      if (listId) {
        const res = await api.get(`/tasks/lists/${listId}/`);
        setTaskList(res.data);
        setRowTeacherDraft((prev) => {
          const next = { ...prev };
          const itemKey = res.data?.items?.find((it) => it.latest_attempt?.id === attemptId)?.id;
          if (itemKey != null) delete next[itemKey];
          return next;
        });
      }
    } catch {
      /* ignore */
    }
  }

  const currentListIndex = taskList?.items?.length
    ? taskList.items.findIndex((i) => {
        const ref = i.task ? taskNavRef(i.task) : String(i.task_id ?? '');
        return ref === taskSlug || String(i.task_id) === taskSlug;
      })
    : -1;
  const prevItem =
    SHOW_TASK_LIST_PREV_NEXT && currentListIndex > 0 ? taskList.items[currentListIndex - 1] : null;
  const nextItem =
    SHOW_TASK_LIST_PREV_NEXT && currentListIndex >= 0 && currentListIndex < taskList.items.length - 1
      ? taskList.items[currentListIndex + 1]
      : null;

  const listNav = taskList?.items?.length > 0 && (
    <div className="task-list-nav">
      <h4>{taskList.title}</h4>
      {canTeacherManageList && (
        <div className="work-teacher-list-manage">
          <div className="work-teacher-list-manage-row">
            <input
              type="search"
              className="form-input"
              placeholder="Поиск задачи по названию…"
              value={addTaskSearch}
              onChange={(e) => setAddTaskSearch(e.target.value)}
            />
            <button
              type="button"
              className="btn btn-outline btn-sm"
              disabled={addTaskSearching || listManageSaving}
              onClick={() => void searchListTasks()}
            >
              {addTaskSearching ? '…' : 'Найти'}
            </button>
          </div>
          {addTaskResults.length > 0 && (
            <ul className="work-teacher-list-search-results">
              {addTaskResults.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    className="btn btn-link btn-sm"
                    disabled={listManageSaving}
                    onClick={() => void appendTaskToListById(t.id)}
                  >
                    + {t.title || t.slug || t.id}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {listManageError ? <div className="text-danger text-sm">{listManageError}</div> : null}
        </div>
      )}
      <ul
        className="task-list-nav-items"
        ref={taskListScrollRef}
        onScroll={saveTaskListScroll}
      >
        {taskList.items.map((item, idx) => {
          const t = item.task;
          const slug = t ? taskNavRef(t) : String(item.task_id ?? '');
          const current = slug === taskSlug || String(item.task_id) === taskSlug;
          let status =
            item.latest_attempt?.self_report_status ?? item.latest_self_report?.self_report_status ?? null;
          if (
            (!status || status === 'NOT_TRIED') &&
            item.latest_attempt?.answer_check_ok === true
          ) {
            status = 'OK_NO_PEEK';
          }
          const report = status || '—';
          const statusClass =
            status === 'EASY' ||
            status === 'OK_NO_PEEK' ||
            status === 'OK_WITH_PEEK' ||
            status === 'OK_WITH_QUESTION'
              ? 'report-positive'
              : status === 'FAILED' || status === 'NO_IDEAS'
              ? 'report-negative'
              : 'report-neutral';
          const attemptId = item.latest_attempt?.id;
          const draftKey = item.id;
          const draftComment =
            rowTeacherDraft[draftKey] !== undefined
              ? rowTeacherDraft[draftKey]
              : (item.latest_attempt?.teacher_comment ?? '');
          return (
            <li key={item.id} className={current ? 'current task-list-item-current' : ''}>
              {isMentorFollowMode ? (
                <div className="task-list-nav-static">
                  <span className="task-list-nav-static-title">
                    {t?.title ?? `Задача ${idx + 1}`}{' '}
                    <span className={`report-badge ${statusClass}`}>{report}</span>
                  </span>
                  {canTeacherManageList && !current ? (
                    <button
                      type="button"
                      className="btn btn-outline btn-xs"
                      disabled={listManageSaving}
                      onClick={() => void removeListItemById(item.id)}
                      title="Удалить из списка"
                    >
                      Удалить
                    </button>
                  ) : null}
                  {isMentorFollowMode && attemptId && taskList.student_id ? (
                    <div className="work-teacher-row-grade">
                      <div className="work-teacher-verdict-btns">
                        {['ACCEPTED', 'REVIEW_IN_LESSON', 'REDO'].map((v) => (
                          <button
                            key={v}
                            type="button"
                            className={`btn btn-xs ${
                              item.latest_attempt?.teacher_verdict === v ? 'btn-primary' : 'btn-outline'
                            }`}
                            onClick={() => void patchTeacherRowAttempt(attemptId, { teacher_verdict: v })}
                          >
                            {TEACHER_VERDICT_LABELS[v]}
                          </button>
                        ))}
                      </div>
                      <textarea
                        className="form-input work-teacher-comment-ta"
                        rows={2}
                        placeholder="Комментарий к последней попытке"
                        value={draftComment}
                        onChange={(e) =>
                          setRowTeacherDraft((prev) => ({ ...prev, [draftKey]: e.target.value }))
                        }
                        onBlur={(e) => {
                          void patchTeacherRowAttempt(attemptId, { teacher_comment: e.target.value });
                        }}
                      />
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="task-list-nav-student-row">
                  <Link
                    to={workUrl(slug)}
                    className="task-list-nav-student-link"
                    onClick={saveTaskListScroll}
                  >
                    {t?.title ?? `Задача ${idx + 1}`}{' '}
                    <span className={`report-badge ${statusClass}`}>{report}</span>
                  </Link>
                  {isOwnListWorker ? (
                    <button
                      type="button"
                      className="btn btn-outline btn-xs task-list-nav-report-btn"
                      title="Отчёт и комментарий по задаче"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setReportModalItem(item);
                      }}
                    >
                      Отчёт
                    </button>
                  ) : null}
                </div>
              )}
            </li>
          );
        })}
        {!isMentorFollowMode && similarTasks.length > 0 && (
          <li className="similar-tasks-group">
            <div className="similar-tasks-title">Похожие задачи</div>
            <ul className="similar-tasks-list">
              {similarTasks.map((st) => {
                const stRef = taskNavRef(st);
                const isCurrent = stRef === taskSlug || String(st.id) === taskSlug;
                return (
                  <li key={st.id} className={isCurrent ? 'current-similar' : ''}>
                    <Link to={workUrl(stRef)} className="similar-task-link">
                      {st.title || stRef}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </li>
        )}
      </ul>
      {SHOW_TASK_LIST_PREV_NEXT && !isMentorFollowMode && (
        <div className="task-list-nav-buttons">
          {prevItem && (
            <Link
              to={workUrl(prevItem.task ? taskNavRef(prevItem.task) : String(prevItem.task_id))}
              className="btn btn-sm btn-outline"
              title="Предыдущая задача"
              onClick={saveTaskListScroll}
            >
              <i className="fa-solid fa-chevron-left ui-icon" aria-hidden />
            </Link>
          )}
          {nextItem && (
            <Link
              to={workUrl(nextItem.task ? taskNavRef(nextItem.task) : String(nextItem.task_id))}
              className="btn btn-sm btn-outline"
              title="Следующая задача"
              onClick={saveTaskListScroll}
            >
              <i className="fa-solid fa-chevron-right ui-icon" aria-hidden />
            </Link>
          )}
        </div>
      )}
    </div>
  );

  /** Боковая колонка: раньше здесь были «История попыток» и панель «Сохранить попытку» (см. git). */
  const historySidebar = (
    <div className="solutions-section" style={{ fontSize: `${workSidebarFontScale}em` }}>
      {listNav}
      {listId && taskList?.id && !isMentorFollowMode && (
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            disabled={listFinishSaving}
            onClick={async () => {
              if (!taskList?.id) return;
              setListFinishSaving(true);
              setListFinishError('');
              try {
                const action = taskList.student_status === 'DONE' ? 'reopen' : 'finish';
                const res = await api.post(`/tasks/lists/${taskList.id}/finish/`, { action });
                setTaskList(res.data);
              } catch {
                setListFinishError('Не удалось изменить статус списка.');
              } finally {
                setListFinishSaving(false);
              }
            }}
            title={taskList?.student_status === 'DONE' ? 'Вернуть список в работу' : 'Сдать список преподавателю'}
          >
            {listFinishSaving ? '...' : (taskList?.student_status === 'DONE' ? 'Вернуть в работу' : 'Сдать список')}
          </button>
          {taskList?.student_finished_at && (
            <span className="text-secondary" style={{ fontSize: '0.9rem' }}>
              Сдано: {new Date(taskList.student_finished_at).toLocaleString('ru-RU')}
            </span>
          )}
        </div>
      )}
      {listFinishError && <div className="alert alert-error" style={{ marginTop: '0.5rem' }}>{listFinishError}</div>}
    </div>
  );

  const inviteUrl =
    user?.is_teacher &&
    !isMentorFollowMode &&
    collabSessionId
      ? `${window.location.origin}/work/${taskSlug}${listId ? `?list=${listId}&collab=${collabSessionId}` : `?collab=${collabSessionId}`}`
      : null;

  const taskStatementBlock = (
    <div className="work-block-statement-content">
      {isCollaborative && (
        <div className="work-session-badge">
          Урок идёт
          {activeLessonFromApi?.work_list_id && !listId ? (
            <>
              {' · '}
              <Link
                to={
                  activeLessonFromApi.work_list_first_task_slug
                    ? `/work/${activeLessonFromApi.work_list_first_task_slug}?list=${activeLessonFromApi.work_list_id}`
                    : '/tasks'
                }
                className="link-like"
              >
                открыть список урока
              </Link>
            </>
          ) : null}
        </div>
      )}
      {teacherWatching && (
        <div className="work-teacher-watching-badge" title="Преподаватель смотрит вашу работу">Преподаватель смотрит</div>
      )}
      {isGuestQuiz && !user ? (
        <p className="text-secondary" style={{ marginBottom: '0.75rem' }}>
          Гостевой режим: ответы не сохраняются.{' '}
          <Link to="/login">Войти</Link>, чтобы вести историю.
        </p>
      ) : null}
      <TaskStatementMeta
        tags={task.tags}
        source_name={task.source_name}
        source_number={task.source_number}
        source_author={task.source_author}
        year={task.year}
      />
      <div style={{ fontSize: `${workStatementFontScale}em` }}>
        {statement}
      </div>
      {isChoiceAnswerType(task.answer_type) ? (
        <ChoiceAnswerPanel
          answerType={task.answer_type}
          answerSpec={task.answer_spec}
          expectedAnswer={task.expected_answer}
          taskKey={taskSlug}
          shuffleSeed={`${listId || ''}:${taskSlug}`}
          shuffle={!isCollaborative}
          value={studentAnswer}
          onChange={setStudentAnswer}
          saving={checkRevealSaving}
          onCheck={canUseListAttempts ? handleCheckAnswer : undefined}
          onReveal={canUseListAttempts ? handleRevealAnswer : undefined}
        />
      ) : null}
      {isOrderAnswerType(task.answer_type) ? (
        <AssembleAnswerPanel
          answerSpec={task.answer_spec}
          expectedAnswer={task.expected_answer}
          taskKey={taskSlug}
          shuffleSeed={`${listId || ''}:${taskSlug}`}
          shuffle={!isCollaborative}
          value={studentAnswer}
          onChange={setStudentAnswer}
          saving={checkRevealSaving}
          onCheck={canUseListAttempts ? handleCheckAnswer : undefined}
          onReveal={canUseListAttempts ? handleRevealAnswer : undefined}
        />
      ) : null}
      {hintsSection}
    </div>
  );

  const workspace = resolveWorkspace(task);
  const showWorkspace = workspace.showWorkspace;
  const showRunner = workspace.showRunner;

  const codeEditorBlock = showWorkspace ? (
    <div className="task-work-editor collaborative">
      {collabSessionId ? (
        <UnifiedWorkspace
          key={`${collabSessionId}:${workspace.kind}`}
          ref={editorRef}
          workspace={workspace}
          sessionId={collabSessionId}
          taskId={task?.id}
          onContentChange={setCode}
          onWorkspaceStateChange={setWorkspaceState}
          seedWorkspaceState={seedWorkspaceState}
          theme={editorTheme}
          editorSettings={editorSettings}
          onYdocReady={setYdoc}
          onRunRequest={() => runRequestRef.current?.run?.()}
          onStatusChange={({ connected, userCount }) => {
            setCollabConnected(connected);
            setCollabUserCount(userCount);
          }}
          styleLintEnabled={styleLintEnabled}
          onStyleLintChange={setStyleLintEnabled}
          onFormattingChange={setFormatting}
        />
      ) : !user ? (
        <div className="editor-connecting">
          <p>Войдите в аккаунт, чтобы открыть редактор кода. В тестах он обычно не нужен.</p>
        </div>
      ) : (
        <div className="editor-connecting">
          <div className="spinner" />
          <p>Подключение к редактору...</p>
        </div>
      )}
    </div>
  ) : null;

  const taskListBlock = historySidebar;

  const inputBlockContent = collabSessionId ? (
    <CollaborativeRunnerInputSection />
  ) : (
    <div className="work-block-placeholder">Подключитесь к редактору</div>
  );

  const outputBlockContent = collabSessionId ? (
    <CollaborativeRunnerOutputSection />
  ) : (
    <div className="work-block-placeholder">—</div>
  );

  function applyPresetA() {
    const next = LAYOUT_PRESET_A.map((item) => ({ ...item }));
    setLayout(next);
    saveLayoutToStorage(layoutStorageKey, next, 'A');
  }

  function applyPresetB() {
    const next = LAYOUT_PRESET_B.map((item) => ({ ...item }));
    setLayout(next);
    saveLayoutToStorage(layoutStorageKey, next, 'B');
  }

  function handleLayoutChange(next) {
    if (layoutsEqual(next, layout)) return;
    setLayout(next);
    saveLayoutToStorage(layoutStorageKey, next, 'custom');
    const taskStatementItem = next.find((i) => i.i === 'taskStatement');
    if (taskStatementItem && !taskStatementCollapsed && typeof taskStatementItem.h === 'number') {
      setTaskStatementCollapsedHeight(taskStatementItem.h);
      saveTaskStatementState(false, taskStatementItem.h);
    }
    const taskListItem = next.find((i) => i.i === 'taskList');
    if (taskListItem && !taskListCollapsed && typeof taskListItem.h === 'number') {
      setTaskListCollapsedHeight(taskListItem.h);
      saveTaskListState(false, taskListItem.h);
    }
  }

  // Высоту в развёрнутом состоянии берём из layout. Не перезаписывать из *CollapsedHeight —
  // иначе при открытии два источника расходятся и react-grid-layout «трясёт» блоки.
  const effectiveLayout = layout.map((item) => {
    if (item.i === 'taskStatement' && taskStatementCollapsed) return { ...item, h: 1 };
    if (item.i === 'taskList' && taskListCollapsed) return { ...item, h: 1 };
    return item;
  });

  function onTaskStatementCollapseToggle() {
    const item = effectiveLayout.find((i) => i.i === 'taskStatement');
    const prevH = item?.h ?? 6;
    setTaskStatementCollapsedHeight(prevH);
    setTaskStatementCollapsed(true);
    const nextLayout = layout.map((i) => (i.i === 'taskStatement' ? { ...i, h: 1 } : i));
    setLayout(nextLayout);
    saveTaskStatementState(true, prevH);
    saveLayoutToStorage(layoutStorageKey, nextLayout, 'custom');
  }

  function onTaskStatementExpand() {
    setTaskStatementCollapsed(false);
    const h = taskStatementCollapsedHeight ?? 6;
    const nextLayout = layout.map((i) => (i.i === 'taskStatement' ? { ...i, h } : i));
    setLayout(nextLayout);
    saveTaskStatementState(false, h);
    saveLayoutToStorage(layoutStorageKey, nextLayout, 'custom');
  }

  function onTaskListCollapseToggle() {
    const item = effectiveLayout.find((i) => i.i === 'taskList');
    const prevH = item?.h ?? 9;
    setTaskListCollapsedHeight(prevH);
    setTaskListCollapsed(true);
    const nextLayout = layout.map((i) => (i.i === 'taskList' ? { ...i, h: 1 } : i));
    setLayout(nextLayout);
    saveTaskListState(true, prevH);
    saveLayoutToStorage(layoutStorageKey, nextLayout, 'custom');
  }

  function onTaskListExpand() {
    setTaskListCollapsed(false);
    const h = taskListCollapsedHeight ?? 9;
    const nextLayout = layout.map((i) => (i.i === 'taskList' ? { ...i, h } : i));
    setLayout(nextLayout);
    saveTaskListState(false, h);
    saveLayoutToStorage(layoutStorageKey, nextLayout, 'custom');
  }

  return (
    <div className={`work-page ${layoutEditMode ? 'layout-edit-mode' : ''}`}>
      {wrapSaveError && <div className="alert alert-warning">{wrapSaveError}</div>}
      {isMentorFollowMode ? (
        <div className="alert alert-info work-mentor-follow-banner" role="status">
          {followJumpNotice ? <p className="work-mentor-follow-notice">{followJumpNotice}</p> : null}
          <div className="work-mentor-follow-actions">
            <span>
              {followEnabled
                ? 'Следование за учеником включено (обновление по REST каждые несколько секунд).'
                : 'Следование выключено: страница не переключается при смене задачи учеником.'}
            </span>
            {studentIdForFollow ? (
              <>
                <Link className="btn btn-outline btn-sm" to={`/teacher/students/${studentIdForFollow}`}>
                  К карточке ученика
                </Link>
                {followEnabled ? (
                  <button type="button" className="btn btn-outline btn-sm" onClick={stopMentorFollow}>
                    Отцепиться
                  </button>
                ) : (
                  <button type="button" className="btn btn-primary btn-sm" onClick={startMentorFollow}>
                    Следовать
                  </button>
                )}
              </>
            ) : (
              <span className="text-sm">Откройте работу по ссылке «Подключиться к работе», чтобы включить слежение.</span>
            )}
          </div>
        </div>
      ) : null}
      <CollaborativeRunnerProvider code={code} ydoc={ydoc} runRef={runRequestRef}>
      <CollaborativeTaskProvider
        ydoc={ydoc}
        editMode={false}
        wrapEnabled={taskTextWrapping}
        sourceText={task.statement || task.description || task.text || 'Описание задачи'}
        sourceTaskId={task.id}
      >
        <RunnerSnapshotFill payloadRef={runnerPayloadRef} />
        <RunnerTaskAttachmentsSync task={task} />
        <Modal open={inputModalOpen} title="Input" onClose={() => setInputModalOpen(false)}>
          {inputBlockContent}
        </Modal>
        <StudentTaskReportModal
          open={Boolean(reportModalItem)}
          onClose={() => setReportModalItem(null)}
          listItem={reportModalItem}
          lessonSessionId={lessonSessionId}
          isCurrentTask={
            reportModalItem
              ? (reportModalItem.task
                  ? taskNavRef(reportModalItem.task)
                  : String(reportModalItem.task_id ?? '')) === taskSlug
              : false
          }
          currentCode={code}
          studentTextAnswer={studentAnswer}
          runnerPayloadRef={runnerPayloadRef}
          onSaved={async () => {
            if (listId) {
              try {
                const res = await api.get(`/tasks/lists/${listId}/`);
                setTaskList(res.data);
              } catch {
                /* ignore */
              }
            }
            if (reportModalItem && currentListItemId && reportModalItem.id === currentListItemId && isOwnListWorker) {
              setAttemptBaseline(
                buildAttemptSnapshot(codeForAttemptBaselineRef.current, studentAnswer, workspaceStateRef.current),
              );
            }
          }}
        />
        <WorkPageWithToolbar
          editorTheme={editorTheme}
          collabConnected={collabConnected}
          collabUserCount={collabUserCount}
          styleLintEnabled={styleLintEnabled}
          onStyleLintChange={setStyleLintEnabled}
          onFormatClick={() => editorRef.current?.format?.()}
          formatting={formatting}
          inviteUrl={inviteUrl}
          onCopyInvite={() => inviteUrl && navigator.clipboard.writeText(inviteUrl)}
          layoutEditMode={layoutEditMode}
          onLayoutEditModeChange={setLayoutEditMode}
          onLayoutPresetA={applyPresetA}
          onLayoutPresetB={applyPresetB}
          onRunClick={() => runRequestRef.current?.run?.()}
          layoutStorageKey={layoutStorageKey}
          layout={effectiveLayout}
          onLayoutChange={handleLayoutChange}
          taskStatementCollapsed={taskStatementCollapsed}
          onTaskStatementCollapseToggle={onTaskStatementCollapseToggle}
          onTaskStatementExpand={onTaskStatementExpand}
          taskListCollapsed={taskListCollapsed}
          onTaskListCollapseToggle={onTaskListCollapseToggle}
          onTaskListExpand={onTaskListExpand}
          showCodeWorkspace={showWorkspace}
          showRunner={showRunner}
          workspaceTitle={workspace.title || 'Черновик'}
          taskStatementTitle={taskStatementTitle}
          taskStatementSupplement={
            <StatementAttachmentLinks attachments={task.statement_attachments} variant="work" />
          }
          taskStatementHeaderActions={
            <>
              {ydoc ? <CollaborativeTaskMarkBar /> : null}
              {!isChoiceAnswerType(task.answer_type) && !isOrderAnswerType(task.answer_type) ? (
                <TaskAnswerCheckPanel
                  variant="header"
                  expectedAnswer={task.expected_answer}
                  taskKey={taskSlug}
                  value={studentAnswer}
                  onChange={setStudentAnswer}
                  saving={checkRevealSaving}
                  onCheck={canUseListAttempts ? handleCheckAnswer : undefined}
                  onReveal={canUseListAttempts ? handleRevealAnswer : undefined}
                />
              ) : null}
              <button
                type="button"
                className={`btn btn-outline btn-xs btn-icon-only work-block-title-action ${taskTextWrapping ? 'active' : ''}`}
                onClick={handleToggleWrapping}
                disabled={wrapSaving || !user}
                title={taskTextWrapping ? 'Выключить перенос строк' : 'Включить перенос строк'}
                aria-label={taskTextWrapping ? 'Перенос включен' : 'Перенос выключен'}
                aria-pressed={taskTextWrapping}
              >
                <i className={`fa-solid ${taskTextWrapping ? 'fa-align-left' : 'fa-ellipsis'} ui-icon`} aria-hidden />
              </button>
            </>
          }
          taskStatement={taskStatementBlock}
          codeEditor={codeEditorBlock}
          taskList={taskListBlock}
          outputBlock={outputBlockContent}
          onOpenInput={() => setInputModalOpen(true)}
          codeEditorHeaderLeadingActions={
            !isMentorFollowMode ? (
              <AttemptSaveHeaderButton
                user={user}
                task={task}
                taskSlug={taskSlug}
                listItemId={getCurrentTaskListItemId()}
                lessonSessionId={lessonSessionId}
                currentCode={code}
                workspaceState={workspaceState}
                studentTextAnswer={studentAnswer}
                runnerPayloadRef={runnerPayloadRef}
                attemptBaseline={attemptBaseline}
                attemptBaselineReady={attemptBaselineReady}
                gateDirtyByBaseline={canTrackOwnWork}
                onBaselineSynced={() => setAttemptBaseline(buildAttemptSnapshot(code, studentAnswer, workspaceState))}
                onAfterSave={() => {
                  if (listId) {
                    api.get(`/tasks/lists/${listId}/`).then((res) => setTaskList(res.data)).catch(() => {});
                  }
                }}
              />
            ) : null
          }
        />
      </CollaborativeTaskProvider>
      </CollaborativeRunnerProvider>
    </div>
  );
}

function WorkPageWithToolbar({
  collabConnected,
  collabUserCount,
  styleLintEnabled,
  onStyleLintChange,
  onFormatClick,
  formatting,
  inviteUrl,
  onCopyInvite,
  layoutEditMode,
  onLayoutEditModeChange,
  onLayoutPresetA,
  onLayoutPresetB,
  onRunClick,
  layoutStorageKey,
  layout,
  onLayoutChange,
  taskStatementCollapsed,
  onTaskStatementCollapseToggle,
  onTaskStatementExpand,
  taskListCollapsed,
  onTaskListCollapseToggle,
  onTaskListExpand,
  showCodeWorkspace = true,
  showRunner = true,
  workspaceTitle = 'Редактор кода',
  taskStatementTitle,
  taskStatementSupplement,
  taskStatementHeaderActions,
  taskStatement,
  codeEditor,
  taskList,
  outputBlock,
  onOpenInput,
  codeEditorHeaderLeadingActions,
}) {
  const { loading, autoClearOutput, setAutoClearOutput, clearOutput } = useRunnerContext();
  return (
    <>
      <WorkToolbar
        connected={collabConnected}
        userCount={collabUserCount}
        styleLintEnabled={styleLintEnabled}
        onStyleLintChange={onStyleLintChange}
        onFormatClick={onFormatClick}
        formatting={formatting}
        inviteUrl={inviteUrl}
        onCopyInvite={onCopyInvite}
        layoutEditMode={layoutEditMode}
        onLayoutEditModeChange={onLayoutEditModeChange}
        onLayoutPresetA={onLayoutPresetA}
        onLayoutPresetB={onLayoutPresetB}
        autoClearOutput={autoClearOutput}
        onAutoClearOutputChange={setAutoClearOutput}
        onClearOutput={clearOutput}
        showCodeTools={showRunner}
      />
      <WorkPageBlocksLayout
        taskStatementTitle={taskStatementTitle}
        taskStatementSupplement={taskStatementSupplement}
        taskStatementHeaderActions={taskStatementHeaderActions}
        taskStatement={taskStatement}
        codeEditor={codeEditor}
        workspaceTitle={workspaceTitle}
        codeEditorHeaderActions={
          showRunner ? (
          <>
            {codeEditorHeaderLeadingActions}
            <button
              type="button"
              className={`btn btn-small work-toolbar-btn work-toolbar-run work-block-title-action ${loading ? 'running' : ''}`}
              onClick={onRunClick}
              disabled={loading}
              title={loading ? 'Остановить программу (пока не реализовано)' : 'Запустить программу (Ctrl+Enter)'}
              aria-label={loading ? 'Выполняется' : 'Запустить'}
            >
              <i className={`fa-solid ${loading ? 'fa-stop' : 'fa-play'} ui-icon`} aria-hidden />
            </button>
            <span className="work-block-title-input-files" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', minWidth: 0 }}>
              <EditorInputFilesBadge />
              <button
                type="button"
                className="btn btn-outline btn-xs btn-icon-only work-block-title-action"
                title="Ввод и файлы"
                aria-label="Ввод и файлы"
                onClick={onOpenInput}
              >
                <i className="fa-solid fa-keyboard ui-icon" aria-hidden />
              </button>
            </span>
          </>
          ) : (
            <>{codeEditorHeaderLeadingActions}</>
          )
        }
        taskList={taskList}
        outputBlock={outputBlock}
        outputBlockRunInfo={<OutputBlockRunInfo />}
        editMode={layoutEditMode}
        layoutStorageKey={layoutStorageKey}
        layout={layout}
        onLayoutChange={onLayoutChange}
        taskStatementCollapsed={taskStatementCollapsed}
        onTaskStatementCollapseToggle={onTaskStatementCollapseToggle}
        onTaskStatementExpand={onTaskStatementExpand}
        taskListCollapsed={taskListCollapsed}
        onTaskListCollapseToggle={onTaskListCollapseToggle}
        onTaskListExpand={onTaskListExpand}
        showAllTasksBlock={false}
        showCodeWorkspace={showCodeWorkspace}
        showRunner={showRunner}
      />
    </>
  );
}
